import { useRef, type PointerEvent, type ReactNode } from 'react'
import { cn } from '#app/utils/misc.tsx'

const pull = { x: 10, y: 8 }

/** Follows the mouse on the short token, springs back with an overshoot. */
const follow = 'transform var(--transition-duration-short) var(--ease-out-expo)'
const springBack =
	'transform var(--transition-duration-medium) var(--ease-spring)'

function clamp(value: number) {
	return Math.min(Math.max(value, -1), 1)
}

/**
 * Magnetic: one interactive child that leans towards a mouse pointer, up to
 * 10px across and 8px up or down, and springs back when the pointer leaves.
 * A padding around the child widens the area that pulls. Touch and pen
 * pointers and reduced motion leave the child in place; the child keeps its
 * focus and keyboard behaviour.
 */
export function Magnetic({
	children,
	className,
}: {
	children: ReactNode
	className?: string
}) {
	const childRef = useRef<HTMLDivElement>(null)

	function lean(event: PointerEvent<HTMLDivElement>) {
		const child = childRef.current
		if (!child || event.pointerType !== 'mouse') return
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
		const box = event.currentTarget.getBoundingClientRect()
		if (!box.width || !box.height) return
		// -1..1 across the box, so its edges pull by the full amount
		const dx = (event.clientX - (box.left + box.width / 2)) / (box.width / 2)
		const dy = (event.clientY - (box.top + box.height / 2)) / (box.height / 2)
		const x = Math.round(clamp(dx) * pull.x * 10) / 10
		const y = Math.round(clamp(dy) * pull.y * 10) / 10
		child.style.transition = follow
		child.style.transform = `translate(${x}px, ${y}px)`
	}

	function release() {
		const child = childRef.current
		if (!child?.style.transform) return
		child.style.transition = springBack
		child.style.transform = 'translate(0px, 0px)'
	}

	return (
		<div
			data-motion="magnetic"
			onPointerMove={lean}
			onPointerLeave={release}
			className={cn('-m-4 p-4', className)}
		>
			<div ref={childRef} className="flex flex-col">
				{children}
			</div>
		</div>
	)
}
